import React, { useEffect, useMemo, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { Button } from "./ui";
import { toast } from "sonner";
import { ArrowRightLeft, Zap, Repeat, Pencil, Play, Trash2, ArrowUpFromLine, ArrowDownToLine } from "lucide-react";

const ACCOUNT_TYPES = ["Income", "Operating", "Payroll", "Petty Cash", "Tax", "Benefits", "Refunds", "Future and Strategies", "Contingencies", "Savings"];
const num = (v) => parseFloat(v) || 0;
const money = (n) => num(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

const emptyForm = (mode) => ({
  fromCode: "", fromId: "", amount: "", conv: "asis", rateIdx: -1,
  toCode: "", toId: "", recurrence: mode === "instant" ? "once" : "scheduled",
  alertAccept: "y", freq: "daily", time: "10", monthlyDate: "1", editingId: null,
});

function TransferCard({ title, mode, icon, pools, yellowRates, seed, applyRate, onDone }) {
  const [f, setF] = useState(emptyForm(mode));
  const [boardTag, setBoardTag] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { if (seed && mode === "rules") setF(seed); }, [seed, mode]);
  useEffect(() => {
    if (!applyRate || !applyRate.rate) return;
    const r = applyRate.rate;
    const idx = yellowRates.findIndex((y) => y.from === r.from && y.to === r.to && y.kind === r.kind && Math.abs(num(y.rate) - num(r.rate)) < 1e-6);
    if (idx >= 0) { setF((s) => ({ ...s, conv: "convert", rateIdx: idx })); setBoardTag({ from: r.from, to: r.to, rate: r.rate, kind: r.kind }); }
  }, [applyRate, yellowRates]);
  const clearBoard = () => { setBoardTag(null); setF((s) => ({ ...s, conv: "asis", rateIdx: -1 })); };

  const codes = useMemo(() => [...new Set(pools.map((p) => (p.code || "").trim()).filter(Boolean))].sort(), [pools]);
  const poolsOf = (code) => pools.filter((p) => (p.code || "") === code);
  const fromPool = pools.find((p) => p.id === f.fromId);
  const toPool = pools.find((p) => p.id === f.toId);
  const chosenRate = f.conv === "convert" && f.rateIdx >= 0 ? num(yellowRates[f.rateIdx]?.rate) : 1;

  const submit = async () => {
    if (!f.fromId || !f.toId) return toast.error("Choose both a From and a To account.");
    if (f.fromId === f.toId) return toast.error("From and To must differ.");
    if (num(f.amount) <= 0) return toast.error("Enter an amount greater than zero.");
    if (f.conv === "convert" && f.rateIdx < 0) return toast.error("Pick one of the flagged (yellow) rates to convert with.");
    if (f.recurrence === "once") {
      try {
        const r = await client.post("/currency/transfer", { from_id: f.fromId, to_id: f.toId, amount: num(f.amount), convert: f.conv === "convert", rate: chosenRate });
        toast.success(`Transferred. Credited ${money(r.data.credited)} to ${toPool?.name}.`);
        setF(emptyForm(mode)); setBoardTag(null); onDone && onDone();
      } catch (e) { toast.error(e?.response?.data?.detail || "Transfer failed."); }
    } else {
      const rule = {
        from_id: f.fromId, from_code: fromPool?.code, from_name: fromPool?.name,
        to_id: f.toId, to_code: toPool?.code, to_name: toPool?.name,
        amount: num(f.amount), convert: f.conv === "convert", rate: chosenRate,
        recurrence: "scheduled", alert_accept: f.alertAccept, freq: f.freq, time: f.time,
        monthly_date: f.monthlyDate, last_actioned: null,
      };
      try {
        if (f.editingId) await client.put(`/transfer_rules/${f.editingId}`, { data: rule });
        else await client.post("/transfer_rules", { data: rule });
        toast.success(f.editingId ? "Rule updated." : "Rule saved.");
        setF(emptyForm(mode)); setBoardTag(null); onDone && onDone();
      } catch (e) { toast.error("Could not save the rule."); }
    }
  };

  const selCls = "w-full rounded border border-black/15 bg-white/70 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-black/30";
  return (
    <div className="rounded-xl border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`transfer-card-${mode}`}>
      <div className="mb-2 flex items-center gap-2"><span className="text-[var(--primary)]">{icon}</span><h4 className="font-head text-sm">{title}</h4></div>

      <label className="mb-1 block text-[10px] font-semibold uppercase opacity-60">{TL("From account")}</label>
      <div className="mb-1 grid grid-cols-2 gap-1">
        <select value={f.fromCode} onChange={(e) => set("fromCode", e.target.value) || setF((s) => ({ ...s, fromCode: e.target.value, fromId: "" }))} data-testid={`tc-${mode}-from-code`} className={selCls}>
          <option value="">{TL("Currency…")}</option>{codes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={f.fromId} onChange={(e) => set("fromId", e.target.value)} data-testid={`tc-${mode}-from-type`} className={selCls}>
          <option value="">{TL("Type…")}</option>{poolsOf(f.fromCode).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div className="mb-2 text-[11px] opacity-70" data-testid={`tc-${mode}-from-balance`}>{fromPool ? `Balance: ${fromPool.code} ${money(fromPool.balance)}` : "—"}</div>

      <label className="mb-1 block text-[10px] font-semibold uppercase opacity-60">{TL("Amount")}</label>
      <input type="number" value={f.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" data-testid={`tc-${mode}-amount`}
        className="mb-2 w-full rounded border border-black/15 bg-white/70 px-2 py-1.5 text-sm tabular-nums dark:border-white/15 dark:bg-black/30" />

      <div className="mb-2 flex gap-3 text-xs">
        <label className="flex items-center gap-1"><input type="radio" name={`conv-${mode}`} checked={f.conv === "asis"} onChange={() => { set("conv", "asis"); setBoardTag(null); }} data-testid={`tc-${mode}-asis`} /> {TL("Transfer as is")}</label>
        <label className="flex items-center gap-1"><input type="radio" name={`conv-${mode}`} checked={f.conv === "convert"} onChange={() => set("conv", "convert")} data-testid={`tc-${mode}-convert`} /> {TL("Transfer & convert")}</label>
      </div>
      {f.conv === "convert" && (
        <div className="mb-2 rounded border border-yellow-400/60 bg-yellow-400/10 p-1.5" data-testid={`tc-${mode}-rate-picker`}>
          {boardTag && (
            <div className="mb-1 flex items-center gap-1.5 text-[10px]" data-testid={`tc-${mode}-board-tag`}>
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--primary)]/15 px-1.5 py-0.5 font-semibold tabular-nums text-[var(--primary)]">
                <span className={`h-2 w-2 flex-none rounded-full ${boardTag.kind === "inverted" ? "bg-red-500" : "bg-yellow-400"}`} title={boardTag.kind === "inverted" ? "Inverted rate" : "Base rate"} data-testid={`tc-${mode}-board-tag-dot`} />
                from the board · {boardTag.from}→{boardTag.to} @ {boardTag.rate} ({boardTag.kind === "inverted" ? "inverted" : "base"})
              </span>
              <button type="button" onClick={clearBoard} data-testid={`tc-${mode}-clear-board`} className="opacity-60 hover:text-red-500 hover:opacity-100">{TL("clear")}</button>
            </div>
          )}
          <div className="mb-1 text-[10px] font-semibold uppercase opacity-60">{TL("Pick a flagged rate")}</div>
          {yellowRates.length === 0 ? <div className="text-[11px] opacity-60">{TL("No flagged rates. Flag up to 3 with “Base” or “Inverted” in the Conversions board above.")}</div> : (
            <div className="flex flex-wrap gap-1">
              {yellowRates.map((r, i) => (
                <button key={i} type="button" onClick={() => { if (i !== f.rateIdx) setBoardTag(null); set("rateIdx", i); }} data-testid={`tc-${mode}-rate-${i}`}
                  className={`rounded px-2 py-1 text-[11px] tabular-nums ${f.rateIdx === i ? "bg-yellow-400 text-black" : "border border-yellow-500/50"}`}>
                  {r.from || "?"}→{r.to || "?"} @ {r.rate || "0"}{r.kind === "inverted" ? " (inv)" : ""}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <label className="mb-1 block text-[10px] font-semibold uppercase opacity-60">{TL("To account")}</label>
      <div className="mb-2 grid grid-cols-2 gap-1">
        <select value={f.toCode} onChange={(e) => setF((s) => ({ ...s, toCode: e.target.value, toId: "" }))} data-testid={`tc-${mode}-to-code`} className={selCls}>
          <option value="">{TL("Currency…")}</option>{codes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={f.toId} onChange={(e) => set("toId", e.target.value)} data-testid={`tc-${mode}-to-type`} className={selCls}>
          <option value="">{TL("Type…")}</option>{poolsOf(f.toCode).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <label className="mb-1 block text-[10px] font-semibold uppercase opacity-60">{TL("Recurrence")}</label>
      <div className="mb-2 flex gap-3 text-xs">
        <label className="flex items-center gap-1"><input type="radio" name={`rec-${mode}`} checked={f.recurrence === "once"} onChange={() => set("recurrence", "once")} data-testid={`tc-${mode}-once`} /> {TL("Once")}</label>
        <label className="flex items-center gap-1"><input type="radio" name={`rec-${mode}`} checked={f.recurrence === "scheduled"} onChange={() => set("recurrence", "scheduled")} data-testid={`tc-${mode}-scheduled`} /> {TL("Scheduled")}</label>
      </div>

      {f.recurrence === "scheduled" && (
        <div className="mb-2 space-y-2 rounded border border-black/10 p-2 dark:border-white/10" data-testid={`tc-${mode}-schedule`}>
          <div className="flex items-center gap-2 text-xs">
            <span className="opacity-70">{TL("Alert to accept rate?")}</span>
            <label className="flex items-center gap-1"><input type="radio" name={`alert-${mode}`} checked={f.alertAccept === "y"} onChange={() => set("alertAccept", "y")} data-testid={`tc-${mode}-alert-y`} /> {TL("Yes")}</label>
            <label className="flex items-center gap-1"><input type="radio" name={`alert-${mode}`} checked={f.alertAccept === "n"} onChange={() => set("alertAccept", "n")} data-testid={`tc-${mode}-alert-n`} /> {TL("No")}</label>
          </div>
          <div className="grid grid-cols-2 gap-1">
            <select value={f.freq} onChange={(e) => set("freq", e.target.value)} data-testid={`tc-${mode}-freq`} className={selCls}>
              <option value="daily">{TL("Daily")}</option>
              <option value="weekly">{TL("Weekly (Tuesday)")}</option>
              <option value="biweekly">{TL("Biweekly (15th)")}</option>
              <option value="monthly">{TL("Monthly (by date)")}</option>
            </select>
            <select value={f.time} onChange={(e) => set("time", e.target.value)} data-testid={`tc-${mode}-time`} className={selCls}>
              <option value="10">{TL("10:00 AM local")}</option>
              <option value="17">{TL("5:00 PM local")}</option>
            </select>
          </div>
          {f.freq === "monthly" && (
            <div className="flex items-center gap-2 text-xs">
              <span className="opacity-70">{TL("On day")}</span>
              <select value={f.monthlyDate} onChange={(e) => set("monthlyDate", e.target.value)} data-testid={`tc-${mode}-monthday`} className="rounded border border-black/15 bg-white/70 px-2 py-1 dark:border-white/15 dark:bg-black/30">
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <span className="opacity-60">{TL("of each month")}</span>
            </div>
          )}
        </div>
      )}

      <Button onClick={submit} data-testid={`tc-${mode}-submit`} className="w-full">
        {f.recurrence === "once" ? TL("Transfer now") : (f.editingId ? TL("Update rule") : TL("Save rule"))}
      </Button>
    </div>
  );
}

export default function TransferAutomation() {
  const [pools, setPools] = useState([]);
  const [rules, setRules] = useState([]);
  const [yellowRates, setYellowRates] = useState([]);
  const [seed, setSeed] = useState(null);
  const [applyReq, setApplyReq] = useState({ instant: null, rules: null });
  const [flt, setFlt] = useState({ code: "", type: "", sd: "" });
  const [sortBy, setSortBy] = useState("date"); // date | amount

  const load = async () => {
    try {
      const [cur, rl, opt] = await Promise.all([client.get("/currencies"), client.get("/transfer_rules"), client.get("/options/currency")]);
      setPools(cur.data || []);
      setRules(rl.data || []);
      const cb = opt.data?.conversion_board;
      const flagged = Array.isArray(cb) ? cb.flat().filter((l) => l && (l.mark === "inverted" || l.mark === "base")).slice(0, 3).map((l) => {
        if (l.mark === "inverted") {
          const inv = parseFloat(l.rate) > 0 ? (1 / parseFloat(l.rate)).toFixed(4) : "";
          return { from: l.to, to: l.from, rate: inv, kind: "inverted" };
        }
        return { from: l.from, to: l.to, rate: l.rate, kind: "base" };
      }) : [];
      setYellowRates(flagged);
    } catch (e) { /* noop */ }
  };
  useEffect(() => {
    load();
    const h = () => load();
    const onApply = (e) => {
      const { card, rate } = e.detail || {};
      if (!card || !rate) return;
      setApplyReq((prev) => ({ ...prev, [card]: { rate, token: Date.now() } }));
    };
    window.addEventListener("fopfo:flags-changed", h);
    window.addEventListener("fopfo:apply-rate", onApply);
    return () => { window.removeEventListener("fopfo:flags-changed", h); window.removeEventListener("fopfo:apply-rate", onApply); };
  }, []);

  const visibleRules = useMemo(() => {
    let list = rules.filter((r) => {
      const codeOk = !flt.code || r.from_code === flt.code || r.to_code === flt.code;
      const typeOk = !flt.type || r.from_name === flt.type || r.to_name === flt.type;
      const sdOk = !flt.sd || !flt.type || (flt.sd === "sweep" ? r.from_name === flt.type : r.to_name === flt.type);
      return codeOk && typeOk && sdOk;
    });
    list = [...list].sort((a, b) => sortBy === "amount" ? (num(b.amount) - num(a.amount)) : String(b.last_actioned || b.created_at || "").localeCompare(String(a.last_actioned || a.created_at || "")));
    return list;
  }, [rules, flt, sortBy]);

  const runRule = async (r) => {
    try {
      await client.post("/currency/transfer", { from_id: r.from_id, to_id: r.to_id, amount: num(r.amount), convert: !!r.convert, rate: num(r.rate) || 1 });
      await client.put(`/transfer_rules/${r.id}`, { data: { ...r, last_actioned: new Date().toISOString() } });
      toast.success("Rule executed.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed."); }
  };
  const editRule = (r) => setSeed({
    fromCode: r.from_code || "", fromId: r.from_id || "", amount: r.amount || "", conv: r.convert ? "convert" : "asis", rateIdx: -1,
    toCode: r.to_code || "", toId: r.to_id || "", recurrence: "scheduled", alertAccept: r.alert_accept || "y",
    freq: r.freq || "daily", time: r.time || "10", monthlyDate: r.monthly_date || "1", editingId: r.id,
  });
  const delRule = async (id) => { try { await client.delete(`/transfer_rules/${id}`); toast.success("Rule deleted."); load(); } catch (e) { /* noop */ } };

  const relTag = (r) => {
    if (flt.type && r.from_name === flt.type) return { sweep: true };
    if (flt.type && r.to_name === flt.type) return { sweep: false };
    return { sweep: null };
  };

  return (
    <div className="mb-3 rounded-xl border border-black/10 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.03]" data-testid="transfer-automation">
      <div className="mb-3 flex items-center gap-2">
        <ArrowRightLeft size={16} className="text-[var(--primary)]" />
        <h3 className="font-head text-base">{TL("Transfer Automation & Rules")}</h3>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <TransferCard title={TL("Instant Transfer")} mode="instant" icon={<Zap size={15} />} pools={pools} yellowRates={yellowRates} applyRate={applyReq.instant} onDone={load} />
        <TransferCard title={TL("Rules Transfer")} mode="rules" icon={<Repeat size={15} />} pools={pools} yellowRates={yellowRates} seed={seed} applyRate={applyReq.rules} onDone={load} />

        <div className="rounded-xl border border-black/10 dark:border-white/10" data-testid="rules-listing">
          <div className="border-b border-black/10 px-2 py-1.5 text-xs font-semibold dark:border-white/10">{TL("Rules — sweeps & drops")}</div>
          <div className="flex flex-wrap items-center gap-1 border-b border-black/10 p-2 dark:border-white/10">
            <select value={flt.code} onChange={(e) => setFlt((s) => ({ ...s, code: e.target.value }))} data-testid="rl-filter-code" className="rounded border border-black/15 bg-white/70 px-1.5 py-1 text-xs dark:border-white/15 dark:bg-black/30">
              <option value="">{TL("All currencies")}</option>{[...new Set(pools.map((p) => p.code).filter(Boolean))].sort().map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={flt.type} onChange={(e) => setFlt((s) => ({ ...s, type: e.target.value }))} data-testid="rl-filter-type" className="rounded border border-black/15 bg-white/70 px-1.5 py-1 text-xs dark:border-white/15 dark:bg-black/30">
              <option value="">{TL("All types")}</option>{[...new Set([...ACCOUNT_TYPES, ...pools.map((p) => p.name).filter(Boolean)])].map((t) => <option key={t} value={t}>{TL(t)}</option>)}
            </select>
            <select value={flt.sd} onChange={(e) => setFlt((s) => ({ ...s, sd: e.target.value }))} data-testid="rl-filter-sd" className="rounded border border-black/15 bg-white/70 px-1.5 py-1 text-xs dark:border-white/15 dark:bg-black/30">
              <option value="">{TL("Sweeps & drops")}</option>
              <option value="sweep">{TL("Sweeps only")}</option>
              <option value="drop">{TL("Drops only")}</option>
            </select>
            <div className="ms-auto flex overflow-hidden rounded border border-black/15 text-[10px] dark:border-white/15">
              <button onClick={() => setSortBy("date")} data-testid="rl-sort-date" className={`px-1.5 py-1 ${sortBy === "date" ? "bg-[var(--primary)] text-white" : "opacity-70"}`}>{TL("Last actioned")}</button>
              <button onClick={() => setSortBy("amount")} data-testid="rl-sort-amount" className={`px-1.5 py-1 ${sortBy === "amount" ? "bg-[var(--primary)] text-white" : "opacity-70"}`}>{TL("Amount")}</button>
            </div>
          </div>
          <div className="max-h-[520px] space-y-1 overflow-y-auto p-2">
            {visibleRules.length === 0 && <div className="py-4 text-center text-xs opacity-50" data-testid="rl-empty">{TL("No rules yet.")}</div>}
            {visibleRules.map((r) => {
              const tag = relTag(r);
              return (
                <div key={r.id} className="rounded border border-black/10 p-1.5 text-[11px] dark:border-white/10" data-testid={`rule-${r.id}`}>
                  <div className="flex items-center gap-1">
                    {tag.sweep === true && <span className="inline-flex items-center gap-0.5 rounded bg-red-500/15 px-1 text-red-600 dark:text-red-400"><ArrowUpFromLine size={11} /> {TL("Sweep")}</span>}
                    {tag.sweep === false && <span className="inline-flex items-center gap-0.5 rounded bg-green-500/15 px-1 text-green-600 dark:text-green-400"><ArrowDownToLine size={11} /> {TL("Drop")}</span>}
                    <b className="tabular-nums">{r.from_code} {money(r.amount)}</b>
                    <span className="opacity-60">{r.from_name} → {r.to_name}</span>
                    <span className="ms-auto opacity-50">{r.freq}{r.time === "17" ? " 5PM" : " 10AM"}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 opacity-70">
                    <span>{r.convert ? `convert @ ${r.rate}` : "as is"}</span>
                    <span>· alert {r.alert_accept === "y" ? "on" : "off"}</span>
                    {r.last_actioned && <span>· last {String(r.last_actioned).slice(0, 10)}</span>}
                    <span className="ms-auto flex items-center gap-2">
                      {r.alert_accept === "y" && <button onClick={() => runRule(r)} title={TL("Accept the current rate and run now (one tap)")} data-testid={`rule-accept-${r.id}`} className="rounded bg-[var(--primary)]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[var(--primary)] hover:bg-[var(--primary)]/25">{TL("✓ Accept rate")}</button>}
                      <button onClick={() => runRule(r)} title={TL("Run now")} data-testid={`rule-run-${r.id}`} className="opacity-70 hover:opacity-100 hover:text-[var(--primary)]"><Play size={13} /></button>
                      <button onClick={() => editRule(r)} title={TL("Quick edit")} data-testid={`rule-edit-${r.id}`} className="opacity-70 hover:opacity-100 hover:text-[var(--primary)]"><Pencil size={13} /></button>
                      <button onClick={() => delRule(r.id)} title={TL("Delete")} data-testid={`rule-del-${r.id}`} className="opacity-70 hover:opacity-100 hover:text-red-500"><Trash2 size={13} /></button>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
